import React, { useState, useMemo } from 'react';
import { STALE_MINUTES } from '../ticketConstants';
import { fmtElapsed, getAgeBand, itemMatchesProfile, getItemColorForProfile } from '../ticketUtils';
import { computeActiveElapsed, computeItemDuration, computeCompletedElapsed, isLaterAddedItem } from '../ticketTimingUtils';
import { sqFsToggleItem, sqFsToggleChefDoneItem, sqFsAssign, sqFsToggleItemAssign, sqFsComplete, sqFsReopen, sqFsRequestHelp } from '../firestoreActions';
import { fmtPhone, telHref } from '../../../utils/formatUtils';
import { useLang, useAutoTranslate } from '../i18n';

// Memoized: the board re-renders on every poll/clock tick, but a card only needs
// to when its own props change. KdsBoardTab keeps those props stable for that.
export const KdsCard = React.memo(function KdsCard({ ticket, now, chefMode, isDelayed = false, delayedItemIdxs = [], activeProfile = null, onHide = null, broadcast = null, onNeedHelp = null, highlighted = false, dimmed = false }) {
  const { t, ln } = useLang();
  const [busy, setBusy] = useState(false);
  const [helpSent, setHelpSent] = useState(false);
  const [helpCooldown, setHelpCooldown] = useState(0);

  // Elapsed time uses per-item fire times (Toast KDS model):
  // shows time since the oldest UNFULFILLED item was fired.
  const mins = useMemo(() => computeActiveElapsed({
    itemFireTimes: ticket.itemFireTimes,
    checkedItems: ticket.checkedItems,
    itemCheckedMs: ticket.itemCheckedMs,
    nowMs: now.getTime(),
  }), [ticket.itemFireTimes, ticket.checkedItems, ticket.itemCheckedMs, now]);
  const items = ticket.items || [];
  // Auto-translate item names AND the free-text prep content (item notes,
  // modifiers, order note) so a Telugu card reads fully in Telugu.
  useAutoTranslate([
    ...items.map(i => i.name),
    ...items.map(i => (i.note || '').trim()),
    ...items.flatMap(i => i.modifiers || []),
    ticket.orderNote,
  ].filter(Boolean));
  const checked = Array.isArray(ticket.checkedItems) ? ticket.checkedItems : [];
  const chefDoneItems = Array.isArray(ticket.chefDoneItems) ? ticket.chefDoneItems : [];
  const assignedItems = Array.isArray(ticket.assignedItems) ? ticket.assignedItems : [];
  const assignmentCoverage = [...new Set([...assignedItems, ...chefDoneItems])];
  const assigned = assignmentCoverage.length > 0;
  const assignedCount = items.filter((_, i) => assignmentCoverage.includes(i)).length;
  const hasAssignedItems = assignedCount > 0;
  const allItemsAssigned = items.length > 0 && assignedCount >= items.length;
  const completeDone = items.filter((_, i) => checked.includes(i)).length;
  const allItemsComplete = items.length > 0 && completeDone >= items.length;
  const chefDone = items.filter((_, i) => assignedItems.includes(i) && chefDoneItems.includes(i)).length;
  const manDone = !!ticket.manuallyCompleted;
  const hasNewItems = !!ticket.hasNewItems;
  const snapshotCount = ticket.snapshotCount || 0;
  const reopenedForNewItems = !!ticket.reopenedForNewItems;
  const allChefDone = items.length > 0 && chefDone >= items.length;

  const status = useMemo(() => {
    if (hasNewItems) return 'open';
    if (manDone) return 'completed';
    if (mins >= STALE_MINUTES) return 'stale';
    if (assigned || hasAssignedItems || checked.length > 0 || chefDoneItems.length > 0) return 'inprogress';
    return 'open';
  }, [hasNewItems, manDone, mins, assigned, hasAssignedItems, checked.length, chefDoneItems.length]);

  const band = status === 'completed' ? 'fresh' : status === 'stale' ? 'stale' : getAgeBand(mins);
  const AGE_COLOR = { fresh: '#10b981', warning: '#f59e0b', medium: '#fb923c', urgent: '#f97316', critical: '#ef4444', stale: '#a855f7' };
  const label = ticket.ticketName || ticket.customerName || `#${ticket.shortId}`;

  // For completed cards: use the pure utility to compute corrected elapsed.
  const completedElapsed = useMemo(() => computeCompletedElapsed({
    timeTakenMin: ticket.timeTakenMin,
    snapshotCount,
    itemCount: items.length,
    itemFireTimes: ticket.itemFireTimes,
    itemCheckedMs: ticket.itemCheckedMs,
    createdAtMs: ticket.createdAt ? new Date(ticket.createdAt).getTime() : null,
  }), [ticket.timeTakenMin, ticket.itemFireTimes, ticket.itemCheckedMs, ticket.createdAt, snapshotCount, items.length]);
  const pickedUp = !!ticket.pickedUp;

  async function handleToggle(idx, wasCk) {
    if (busy || manDone) return;
    setBusy(true);
    try {
      const _next = wasCk ? checked.filter(i => i !== idx) : [...new Set([...checked, idx])];
      if (broadcast) broadcast(ticket.id, { checkedItems: _next });
      await sqFsToggleItem(ticket.id, idx, wasCk, checked, items, ticket.createdAt, ticket.itemCheckedMs, {
        prepCompletedAt: ticket.prepCompletedAt, prepTimeMin: ticket.prepTimeMin,
        prepSource: ticket.prepSource, chefDoneMs: ticket.chefDoneMs,
      });
    } finally { setBusy(false); }
  }

  async function handleChefDone(idx) {
    if (busy || manDone) return;
    setBusy(true);
    try { await sqFsToggleChefDoneItem(ticket.id, idx, chefDoneItems, assignedItems, items, ticket.createdAt, ticket.chefDoneMs); }
    finally { setBusy(false); }
  }

  async function handleAssign() {
    if (busy || manDone) return;
    setBusy(true);
    try {
      const _newAssigned = !allItemsAssigned;
      const _allIdx = _newAssigned ? items.map((_, i) => i) : [];
      if (broadcast) broadcast(ticket.id, { assigned: _newAssigned, assignedItems: _allIdx });
      await sqFsAssign(ticket.id, !allItemsAssigned, items.length);
    } finally { setBusy(false); }
  }

  async function handleComplete() {
    if (busy) return;
    setBusy(true);
    try {
      if (manDone) {
        if (broadcast) broadcast(ticket.id, { manuallyCompleted: false, checkedItems: [], assignedItems: [] });
        await sqFsReopen(ticket.id);
      } else {
        if (broadcast) broadcast(ticket.id, { manuallyCompleted: true, checkedItems: items.map((_, i) => i) });
        await sqFsComplete(ticket.id, items, ticket.createdAt, label, ticket.total, ticket.itemCheckedMs, {
          prepCompletedAt: ticket.prepCompletedAt, prepTimeMin: ticket.prepTimeMin,
          prepSource: ticket.prepSource, chefDoneMs: ticket.chefDoneMs,
        });
      }
    } finally { setBusy(false); }
  }

  async function handleNeedHelp() {
    if (helpSent || helpCooldown > 0) return;
    setHelpSent(true);
    setHelpCooldown(60);
    const interval = setInterval(() => {
      setHelpCooldown(prev => { if (prev <= 1) { clearInterval(interval); return 0; } return prev - 1; });
    }, 1000);
    try {
      await sqFsRequestHelp({ orderId: ticket.id, ticketName: label, employeeName: 'Staff', message: '' });
      if (onNeedHelp) onNeedHelp(ticket.id, label);
    } catch {}
    setTimeout(() => setHelpSent(false), 5000);
  }

  return (
    <div className={`kds__card kds__card--${band}${manDone ? ' kds__card--done' : ''}${busy ? ' kds__card--busy' : ''}${chefMode ? ' kds__card--chef' : ''}${isDelayed ? ' kds__card--delayed' : ''}${pickedUp ? ' kds__card--picked-up' : ''}${highlighted ? ' kds__card--highlight' : ''}${dimmed ? ' kds__card--dim' : ''}`}>
      {/* Top bar */}
      <div className="kds__top">
        <span className="kds__label">{label}</span>
        {isDelayed && <span className="kds__delay-badge">{t('card.late')}</span>}
        {pickedUp && <span className="kds__pickedup-badge">{t('card.pickedUp')}</span>}
        {manDone
          ? completedElapsed != null && (
              <span className="kds__elapsed" style={{ color: '#10b981' }}>✅ {fmtElapsed(completedElapsed)}</span>
            )
          : <span className="kds__elapsed" style={{ color: AGE_COLOR[band] }}>{fmtElapsed(mins)}</span>
        }
      </div>

      {/* Online-order details — payment badge, tap-to-call phone, customer note.
          Payment status is its own chip (never the name — see square-tickets.js
          parseOrder for why the name must always win the label). */}
      {(ticket.isOnlineOrder || ticket.customerPhone) && (
        <div className="kds__online-row">
          {ticket.isOnlineOrder && (
            <span className={`kds__paychip${ticket.payAtStore ? ' kds__paychip--due' : ' kds__paychip--paid'}`}>
              {ticket.payAtStore ? t('card.payAtPickup') : t('card.paidOnline')}
            </span>
          )}
          {ticket.customerPhone && (
            <a
              className="kds__phone"
              href={telHref(ticket.customerPhone)}
              onClick={(e) => e.stopPropagation()}
              title={t('card.callCustomer')}>
              📞 {fmtPhone(ticket.customerPhone)}
            </a>
          )}
        </div>
      )}
      {ticket.orderNote && (
        <div className="kds__ordernote">📝 {ln(ticket.orderNote)}</div>
      )}

      {/* Items */}
      <ul className="kds__items">
        {items.length === 0 && <li className="kds__item--empty">{t('card.noItems')}</li>}
        {items.map((item, idx) => {
          const isCk = checked.includes(idx);
          const isChefDone = chefDoneItems.includes(idx);
          const isAsg = assignedItems.includes(idx);
          const isNewItem = (reopenedForNewItems || hasNewItems) && snapshotCount > 0 && idx >= snapshotCount;
          const isMyItem = chefMode && activeProfile && itemMatchesProfile(item.name, activeProfile) && !isChefDone;
          const isItemLate = !isDelayed && !isCk && !manDone && delayedItemIdxs.includes(idx);
          const metaTags = [...(item.modifiers || [])].filter(Boolean);
          const noteText = (item.note || '').trim() || null;

          return (
            <li key={idx}
              className={`kds__item${isCk || manDone ? ' kds__item--done' : ''}${isChefDone ? ' kds__item--chef-done' : ''}${isAsg && !chefMode ? ' kds__item--assigned' : ''}${chefMode ? ' kds__item--chef' : ''}${isNewItem ? ' kds__item--new' : ''}${isMyItem ? ' kds__item--mine' : ''}${isItemLate ? ' kds__item--late' : ''}`}
              style={isMyItem ? { '--mine-color': getItemColorForProfile(item.name, activeProfile), '--mine-color-dim': getItemColorForProfile(item.name, activeProfile) + '28' } : undefined}
            >
              {chefMode ? (
                <div className="kds__item-lbl kds__item-lbl--chef">
                  <ItemNameBlock item={item} idx={idx} isNewItem={isNewItem} isItemLate={isItemLate} metaTags={metaTags} noteText={noteText} />
                </div>
              ) : (
                <div className="kds__item-lbl kds__item-tap" role="button" tabIndex={0}
                  onClick={() => !busy && !manDone && handleToggle(idx, isCk)}
                  onKeyDown={e => e.key === 'Enter' && !busy && !manDone && handleToggle(idx, isCk)}>
                  <ItemNameBlock item={item} idx={idx} isNewItem={isNewItem} isItemLate={isItemLate} metaTags={metaTags} noteText={noteText} />
                </div>
              )}

              {/* Per-item timing badge — only on items added/updated after the
                  order was placed, so original lines don't just repeat the card
                  age. `isNewItem` covers the reopened/completed flow; the fire-time
                  check covers items added to a still-active ticket. */}
              {(() => {
                const fireMs = ticket.itemFireTimes?.[idx];
                const createdMs = ticket.createdAt ? new Date(ticket.createdAt).getTime() : null;
                const baseMs = fireMs ?? createdMs;

                const isUpdatedItem = isNewItem || isLaterAddedItem({ fireMs, createdMs });
                if (!isUpdatedItem) return null;

                if (isCk || manDone) {
                  // Checked/completed item: show how long it took (fire → check)
                  const duration = computeItemDuration({
                    itemIndex: idx,
                    itemFireTimes: ticket.itemFireTimes,
                    itemCheckedMs: ticket.itemCheckedMs,
                    createdAtMs: createdMs,
                  });
                  const display = duration != null ? duration : (manDone ? ticket.timeTakenMin : null);
                  const isNewIdx = snapshotCount > 0 && idx >= snapshotCount;
                  return display != null ? (
                    <span className="kds__item-dur" title={isNewIdx ? t('card.itemDurAdded') : t('card.itemDurCheck')}>
                      {fmtElapsed(display)}
                    </span>
                  ) : null;
                }

                if (!manDone && baseMs) {
                  // Unchecked active item: show live elapsed since fired
                  const liveMin = Math.round((now.getTime() - baseMs) / 60000);
                  return (
                    <span className="kds__item-dur kds__item-dur--live" title={t('card.itemWaiting')}>
                      {fmtElapsed(liveMin)}
                    </span>
                  );
                }

                return null;
              })()}

              {/* Chef mode: chef-done button */}
              {!manDone && chefMode && (
                <button
                  className={`kds__item-chef-btn${isChefDone ? ' kds__item-chef-btn--on' : ''}`}
                  title={isChefDone ? t('card.chefUndo') : t('card.chefMark')}
                  onClick={() => handleChefDone(idx)}
                  disabled={busy}>
                  👨‍🍳
                </button>
              )}

              {/* Front desk mode: assign/status icons */}
              {!manDone && !chefMode && (
                isChefDone ? (
                  <span className="kds__item-status-icon kds__item-status-icon--prep" title={t('card.chefPrepDone')}>👨‍🍳</span>
                ) : isAsg ? (
                  <button className="kds__item-status-icon kds__item-status-icon--assigned kds__item-status-icon--button"
                    title={t('card.assignedClick')}
                    onClick={() => sqFsToggleItemAssign(ticket.id, idx, assignedItems)} disabled={busy}>👨‍🍳</button>
                ) : (
                  <button className={`kds__item-assign-btn${isAsg ? ' kds__item-assign-btn--on' : ''}`}
                    title={t('card.assignItem')}
                    onClick={() => sqFsToggleItemAssign(ticket.id, idx, assignedItems)} disabled={busy}>👤</button>
                )
              )}
            </li>
          );
        })}
      </ul>

      {/* Action buttons */}
      <div className="kds__actions">
        {chefMode ? (
          <>
            <div className={`kds__chef-status${manDone ? ' kds__chef-status--done' : ''}`}>
              {manDone ? (
                <span className="kds__chef-status--all">{t('card.frontDesk')}</span>
              ) : allChefDone ? (
                <span className="kds__chef-status--all">{t('card.prepDoneSent')}</span>
              ) : (
                <span className="kds__chef-status--pending">{t('card.prepProgress', { done: chefDone, total: items.length })}</span>
              )}
              {onHide && <button className="kds__hide-btn" onClick={() => onHide(ticket.id)} title={t('card.hideTitle')}>{t('card.hide')}</button>}
            </div>
            {manDone && (
              <button className="kds__btn kds__btn--reopen kds__btn--compact" onClick={handleComplete} disabled={busy}>{t('card.reopen')}</button>
            )}
          </>
        ) : (
          <>
            {!manDone && (
              <button
                className={`kds__btn kds__btn--assign${assigned ? ' kds__btn--assigned' : ''}${allItemsAssigned ? ' kds__btn--all-assigned' : ''}`}
                onClick={handleAssign}
                disabled={busy || allItemsComplete}
                title={allItemsComplete ? t('card.assignAllComplete') : allItemsAssigned ? t('card.unassignAll') : t('card.assignAll')}>
                {allItemsAssigned ? t('card.unassignAllBtn') : hasAssignedItems ? t('card.assignAllN', { done: assignedCount, total: items.length }) : t('card.assign')}
              </button>
            )}
            <button
              className={`kds__btn${manDone ? ' kds__btn--reopen' : ' kds__btn--complete'}`}
              onClick={handleComplete} disabled={busy}
              title={manDone ? t('card.reopenTitle') : t('card.completeTitle')}>
              {manDone ? t('card.reopen') : completeDone > 0 && completeDone < items.length ? t('card.completeN', { done: completeDone, total: items.length }) : t('card.complete')}
            </button>
          </>
        )}
      </div>
    </div>
  );
});

// Extracted inline sub-component for item name + modifiers + notes
function ItemNameBlock({ item, isNewItem, isItemLate, metaTags, noteText }) {
  const { t, ln } = useLang();
  return (
    <span className="kds__item-name-wrap">
      <span className="kds__item-name">
        {item.qty > 1 && <span className="kds__qty">×{item.qty} </span>}
        {ln(item.name)}
        {isNewItem && <span className="kds__item-new-badge" />}
        {isItemLate && <span className="kds__item-late-badge">{t('card.late')}</span>}
      </span>
      {metaTags.length > 0 && (
        <span className="kds__item-mods">
          {metaTags.map((m, mi) => <span key={mi} className="kds__mod-tag">{ln(m)}</span>)}
        </span>
      )}
      {noteText && (
        <span className="kds__item-note">
          <span className="kds__item-note-lbl">{t('card.noteLabel')}</span> {ln(noteText)}
        </span>
      )}
    </span>
  );
}
